// Tokens the workspace writes into the close-loop templates. The send path
// resolves them; anything still unresolved blocks the mail.
export const CLOSE_LOOP_TOKENS = {
	contactFirstName: "{{ContactFirstName}}",
	shippedItem: "{{ShippedItem}}",
	upvotedItem: "{{UpvotedItem}}",
	portalName: "{{PortalName}}",
	shippedBy: "{{ShippedBy}}",
	agentName: "{{AgentName}}",
} as const

export const CLOSE_LOOP_CONTACT_FALLBACK = "there"

// Never the stored Issue.title: a fallback to internal text is the leak.
export const CLOSE_LOOP_ITEM_FALLBACK = "what you reported"

export const CLOSE_LOOP_SHIPPED_BY_FALLBACK = "the engineering team"

export const CLOSE_LOOP_AGENT_FALLBACK = "The Productlane Agent"

export const defaultCloseLoopTemplate = `Hey ${CLOSE_LOOP_TOKENS.contactFirstName},

Good news: we just shipped ${CLOSE_LOOP_TOKENS.shippedItem}.

Give it a try and tell us how it goes. A human on the team reads every reply.

Cheers,
${CLOSE_LOOP_TOKENS.agentName}`

export const defaultCloseLoopProjectTemplate = `Hey ${CLOSE_LOOP_TOKENS.contactFirstName},

Good news: ${CLOSE_LOOP_TOKENS.shippedItem} is now live. Your feedback helped shape it.

Give it a try and tell us how it goes. A human on the team reads every reply.

Cheers,
${CLOSE_LOOP_TOKENS.agentName}`

export const defaultCloseLoopUpvoteTemplate = `Hey ${CLOSE_LOOP_TOKENS.contactFirstName},

you upvoted ${CLOSE_LOOP_TOKENS.upvotedItem} on ${CLOSE_LOOP_TOKENS.portalName}'s portal. It is live now.

Cheers,
${CLOSE_LOOP_TOKENS.agentName}`

export const firstName = (name?: string | null): string => {
	const first = name?.trim().split(/\s+/)[0]
	return first && first.length > 0 ? first : CLOSE_LOOP_CONTACT_FALLBACK
}

const pickTemplate = (template: string | null | undefined, fallback: string) =>
	template && template.trim().length > 0 ? template.trim() : fallback

export type CloseLoopSource = "issue" | "project"

// Each source falls back to its own default, so editing one message leaves the other alone.
export const closeLoopReportedTemplateFor = (
	source: CloseLoopSource,
	{
		template,
		projectTemplate,
	}: { template?: string | null; projectTemplate?: string | null },
): string =>
	source === "project"
		? pickTemplate(projectTemplate, defaultCloseLoopProjectTemplate)
		: pickTemplate(template, defaultCloseLoopTemplate)

const orFallback = (value: string | null | undefined, fallback: string) =>
	value?.trim() || fallback

interface SharedTokenInput {
	contactName?: string | null
	/// Display name of the Linear assignee or project lead; never a customer.
	shippedBy?: string | null
	agentName?: string | null
}

const fillShared = (
	body: string,
	{ contactName, shippedBy, agentName }: SharedTokenInput,
) =>
	body
		.replaceAll(CLOSE_LOOP_TOKENS.contactFirstName, firstName(contactName))
		.replaceAll(
			CLOSE_LOOP_TOKENS.shippedBy,
			orFallback(shippedBy, CLOSE_LOOP_SHIPPED_BY_FALLBACK),
		)
		.replaceAll(
			CLOSE_LOOP_TOKENS.agentName,
			orFallback(agentName, CLOSE_LOOP_AGENT_FALLBACK),
		)

// The how-to joins the sentence that names the shipped item, so every template
// gets it without a token of its own.
export const insertAfterItemSentence = (
	template: string,
	token: string,
	sentence: string | null | undefined,
): string => {
	const extra = sentence?.trim()
	const at = template.indexOf(token)
	if (!extra || at === -1) return template
	const rest = template.slice(at + token.length)
	const end = rest.search(/[.!?](?=\s|$)/)
	const lineEnd = rest.search(/\n/)
	const cut =
		end !== -1 && (lineEnd === -1 || end < lineEnd)
			? at + token.length + end + 1
			: lineEnd !== -1
				? at + token.length + lineEnd
				: template.length
	return `${template.slice(0, cut)} ${extra}${template.slice(cut)}`
}

/// The sentence a phrase fills, so a check can read it the way the customer will.
export const closeLoopItemSentence = (
	template: string,
	phrase: string,
): string =>
	template
		.replaceAll(CLOSE_LOOP_TOKENS.shippedItem, phrase)
		.split(/\n+|(?<=[.!?])\s+/)
		.map((sentence) => sentence.trim())
		.find((sentence) => sentence.includes(phrase)) ?? phrase

export interface CloseLoopTemplateInput extends SharedTokenInput {
	template?: string | null
	/// Public wording only: Issue.publicTitle or Project.publicTitle, never the internal title.
	publicItemName?: string | null
	/// A phrase the close-loop AI wrote and screened; used only when there is no public title.
	generatedItemName?: string | null
	/// One screened sentence on where to find or turn on what shipped.
	howToUse?: string | null
}

export const renderCloseLoopTemplate = ({
	template,
	publicItemName,
	generatedItemName,
	howToUse,
	...shared
}: CloseLoopTemplateInput): string =>
	fillShared(
		insertAfterItemSentence(
			pickTemplate(template, defaultCloseLoopTemplate),
			CLOSE_LOOP_TOKENS.shippedItem,
			howToUse,
		).replaceAll(
			CLOSE_LOOP_TOKENS.shippedItem,
			orFallback(
				publicItemName,
				orFallback(generatedItemName, CLOSE_LOOP_ITEM_FALLBACK),
			),
		),
		shared,
	)

export interface CloseLoopUpvoteTemplateInput extends SharedTokenInput {
	template?: string | null
	/// Public wording only. Null means no mail: "you upvoted what you reported" reads worse than silence.
	publicItemName: string | null | undefined
	portalName: string
	howToUse?: string | null
}

export const renderCloseLoopUpvoteTemplate = ({
	template,
	publicItemName,
	portalName,
	howToUse,
	...shared
}: CloseLoopUpvoteTemplateInput): string | null => {
	const item = publicItemName?.trim()
	if (!item) return null
	return fillShared(
		insertAfterItemSentence(
			pickTemplate(template, defaultCloseLoopUpvoteTemplate),
			CLOSE_LOOP_TOKENS.upvotedItem,
			howToUse,
		)
			.replaceAll(CLOSE_LOOP_TOKENS.upvotedItem, item)
			.replaceAll(CLOSE_LOOP_TOKENS.portalName, portalName.trim()),
		shared,
	)
}

export const templateUsesShippedBy = (template: string | null | undefined) =>
	!!template?.includes(CLOSE_LOOP_TOKENS.shippedBy)

export const hasUnresolvedTokens = (body: string): boolean =>
	/\{\{[A-Za-z]+\}\}/.test(body)

const escapeHtml = (text: string) =>
	text
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;")
		.replaceAll('"', "&quot;")

export const closeLoopBodyToHtml = (body: string): string =>
	body
		.split(/\n\s*\n/)
		.map((paragraph) => paragraph.trim())
		.filter(Boolean)
		.map(
			(paragraph) =>
				`<p style="margin:0 0 16px 0;">${escapeHtml(paragraph).replaceAll("\n", "<br>")}</p>`,
		)
		.join("")

export type CloseLoopTemplateKind = "reported" | "upvoted"

export interface CloseLoopPlaceholder {
	key: string
	label: string
}

const SHARED_PLACEHOLDERS: CloseLoopPlaceholder[] = [
	{ key: "ContactFirstName", label: "Customer's first name" },
	{ key: "ShippedBy", label: "Linear assignee or project lead" },
	{ key: "AgentName", label: "Your agent's name" },
]

export const CLOSE_LOOP_PLACEHOLDERS: Record<
	CloseLoopTemplateKind,
	CloseLoopPlaceholder[]
> = {
	reported: [
		SHARED_PLACEHOLDERS[0]!,
		{ key: "ShippedItem", label: "What shipped" },
		...SHARED_PLACEHOLDERS.slice(1),
	],
	upvoted: [
		SHARED_PLACEHOLDERS[0]!,
		{ key: "UpvotedItem", label: "Public title of what they upvoted" },
		{ key: "PortalName", label: "Portal the vote came from" },
		...SHARED_PLACEHOLDERS.slice(1),
	],
}

export const closeLoopPlaceholderToken = (key: string) => `{{${key}}}`
